/* eslint-disable react-refresh/only-export-components --
   This module intentionally colocates the SoftphoneProvider component with its
   useSoftphone hook and shared types — the standard context-provider pattern.
   Only affects Fast Refresh granularity in dev, not correctness. */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type {
  Call as TwilioCall,
  Device as TwilioDevice
} from "@twilio/voice-sdk";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { voiceApi, type CallSummary } from "api/voice";
import { logger } from "logger";

export type SoftphoneStatus =
  // device lifecycle
  | "idle"
  | "registering"
  | "ready"
  | "error"
  | "mic-denied"
  // inbound call ringing in the browser (CRM-905)
  | "incoming"
  // live call
  | "connecting"
  | "in-call"
  | "reconnecting"
  | "ending"
  // terminal call outcomes (shown on the call card until dismissed)
  | "no-answer"
  | "call-ended"
  | "unreachable";

// How the terminal error toast can offer recovery. Retrying in place fixes a
// dead device; it cannot fix a stale build or an account without calling, and
// offering it there just teaches users the button does nothing.
export type SoftphoneErrorAction = "reconnect" | "reload" | "none";

// Statuses where a call is live (or ringing) and the user can't start another.
export const LIVE_CALL_STATUSES: SoftphoneStatus[] = [
  "incoming",
  "connecting",
  "in-call",
  "reconnecting",
  "ending"
];

// Maps the backend's authoritative call status to the terminal UI state.
const OUTCOME_STATUS_MAP: Partial<
  Record<CallSummary["status"], SoftphoneStatus>
> = {
  completed: "call-ended",
  no_answer: "no-answer",
  busy: "no-answer",
  failed: "unreachable",
  canceled: "unreachable",
  expired_before_dial: "unreachable"
};

export interface ActiveCall {
  callId: number;
  direction: "outbound" | "inbound";
  // The external party's number: the dialed number for outbound calls, the
  // caller for inbound — the number the card/pill displays.
  toNumber: string;
  leadId?: number;
  startedAt: Date;
  // Set when Twilio fires `accept` — the basis for the in-call duration timer
  // and the fallback "call ended" duration if the backend lookup is unavailable.
  answeredAt?: Date;
  twilioCall: TwilioCall;
  // Display metadata for the call card / pill. `fromNumber` is resolved
  // server-side (authorize response); labels are supplied by the caller.
  fromNumber?: string;
  fromLabel?: string;
  toLabel?: string;
}

// An inbound call ringing in the browser (CRM-905). Metadata arrives as TwiML
// <Client><Parameter> custom parameters set by the inbound TwiML endpoint.
export interface IncomingCall {
  twilioCall: TwilioCall;
  // Absent only if the custom parameters were dropped — the popup still works
  // off the SDK-provided caller number; outcome resolution is skipped.
  callId?: number;
  fromNumber: string;
  toNumber?: string;
  leadId?: number;
  leadName?: string;
  // Pipeline stage shared by every lead matching the caller, with that stage's
  // own color (CRM-1221). Both absent unless the status is unambiguous — the
  // caller matched one lead, or several that all sit in the same stage.
  leadStage?: string;
  leadStageColor?: string;
  // Friendly label of the called provisioned number (e.g. the property line).
  numberLabel?: string;
}

interface SoftphoneContextValue {
  status: SoftphoneStatus;
  errorMessage: string | null;
  // Which recovery affordance the terminal error should offer.
  errorAction: SoftphoneErrorAction;
  activeCall: ActiveCall | null;
  // The inbound call currently ringing in this browser (CRM-905).
  incomingCall: IncomingCall | null;
  isMuted: boolean;
  // True when the device is registered and no call is live — i.e. placeCall
  // will succeed. Stays true while a terminal call card is still on screen.
  isReady: boolean;
  // Resolved duration for the "Call ended · MM:SS" terminal state.
  callDurationSeconds: number | null;
  placeCall: (params: {
    toNumber: string;
    leadId?: number;
    // The CRM contact the call is placed from. Pins the call's identity on the
    // server so the call row, its inbox thread and the notification name the
    // same person; when absent the api resolves it from the number.
    contactId?: number;
    customerId?: number;
    fromNumberId?: number;
    fromLabel?: string;
    toLabel?: string;
  }) => Promise<void>;
  // Answer / decline the ringing inbound call.
  acceptIncoming: () => void;
  rejectIncoming: () => void;
  hangup: () => void;
  toggleMute: () => void;
  // Sends DTMF tones on the live call; false when the SDK never got them.
  sendDtmf: (digits: string) => boolean;
  // Clears a terminal call card and returns the softphone to `ready`.
  dismissCall: () => void;
  retryRegistration: () => void;
  // Marks a lead created mid-call so the in-call card flips to "Contact details".
  attachLeadToActiveCall: (lead: { id: number; name?: string }) => void;
}

const SoftphoneContext = createContext<SoftphoneContextValue | null>(null);

// Refresh the capability token 10 min before expiry. Server issues a 1-hour
// token, so we refresh comfortably early while also leaving a large guard
// against clock skew.
const TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000;
const TOKEN_REFRESH_MIN_DELAY_MS = 30_000;
const TOKEN_REFRESH_RETRY_DELAYS_MS = [5_000, 15_000, 60_000] as const;
// setTimeout stores its delay in a signed 32-bit int: anything larger overflows
// and fires immediately. A skewed clock can produce exactly that.
const MAX_TIMER_DELAY_MS = 2_147_483_647;
// Used when `expires_at` is missing or unparseable — the server issues a
// one-hour token, so this still refreshes comfortably inside its lifetime
// instead of scheduling from a NaN (which would fire in a hot loop).
const TOKEN_REFRESH_FALLBACK_DELAY_MS = 45 * 60 * 1000;
// After the browser reports connectivity is back, wait a beat before dialing
// out — right after wake-from-sleep the `online` event often precedes working
// DNS/routing by a second or two.
const ONLINE_RECOVERY_GRACE_MS = 2_000;
// Backoff for rebuilding a device ourselves. The last rung repeats forever: a
// rebuild is cheap and idempotent while the softphone is idle, so there is no
// point ever giving up and leaving the user a dead phone plus a manual button.
// Prior to CRM-1073 the ladder stopped after two rungs, which is why a bad
// minute produced a toast that then sat there (the counter was never reset, so
// even the Reconnect button got no retries afterwards).
const RECOVERY_BACKOFF_MS = [2_000, 5_000, 10_000, 20_000, 30_000, 60_000];
// How long a fault must persist before the user hears about it. Below this the
// softphone recovers in silence: an abnormal websocket close is routine (Wi-Fi
// roam, VPN reconnect, proxy idle-kill, a late Twilio heartbeat) and the SDK
// heals it on its own, so a "voice service disconnected" toast for one is pure
// noise — the actual CRM-1073 complaint.
const SUSTAINED_FAULT_MS = 60_000;
// A transport-level error means the SDK's own reconnect is ALREADY running
// (WSTransport retries with backoff indefinitely and the device re-registers
// itself once signaling is back). Give that a chance to land before taking
// over — rebuilding the device here would destroy the recovery in flight.
const TRANSPORT_HEAL_GRACE_MS = 20_000;
// `Device.register()` has no deadline of its own: a signaling socket that opens
// but never completes registration leaves the promise pending forever, which
// strands the softphone in "registering" with no fault and no recovery.
const REGISTRATION_TIMEOUT_MS = 20_000;
// Debounce for the wake-up heal: `focus` and `visibilitychange` fire together
// when the user returns to the tab.
const WAKE_HEAL_COOLDOWN_MS = 5_000;

// Twilio errors that mean "the signaling websocket blipped". The SDK owns
// recovery for every one of these, so the only correct action is to wait.
// 31000 socket error · 31005 abnormal close (1006/1015) · 31009 publish with no
// transport (the 30s registration heartbeat firing mid-outage) · 53000-53002
// generic signaling errors (everything collapses here unless the SDK's improved
// error precision is on).
const TRANSPORT_ERROR_CODES = new Set<number>([
  31000, 31005, 31009, 53000, 53001, 53002
]);

// Token errors (20101-20104, 31201-31205) deliberately have no set of their
// own: they land in the generic recoverable path, whose rebuild mints a fresh
// token — exactly the recovery they need.

// getUserMedia was refused outright — the user has to re-grant permission.
const MIC_DENIED_CODES = new Set<number | string>([
  31401, // UserMediaDenied
  "NotAllowedError",
  "PermissionDeniedError",
  "SecurityError"
]);

// The mic exists and is permitted but could not be opened — almost always
// another app (Zoom, Teams, Meet) holding the device. Distinct copy: telling
// someone to grant a permission they already granted sends them in circles.
const MIC_UNAVAILABLE_CODES = new Set<number | string>([
  31208, // AcquisitionFailedError
  31402, // UserMediaFailed
  "NotReadableError",
  "NotFoundError",
  "OverconstrainedError",
  "AbortError"
]);

// A tab left open across a deploy runs a build whose lazily-imported chunk is
// gone from the CDN, so `import("@twilio/voice-sdk")` 404s. No amount of
// retrying fixes that — only reloading the page does.
const STALE_BUILD_MESSAGE_RE =
  /dynamically imported module|importing a module script failed|failed to fetch dynamically imported/i;

// The mic-fault copy doubles as the toast body, so each string must carry its
// own recovery instruction — the toast title is generic.
const MIC_DENIED_MESSAGE =
  "Microphone access is required. Click the lock icon next to the URL, allow microphone access, then retry.";
const MIC_UNAVAILABLE_MESSAGE =
  "Your microphone is unavailable — another app may be using it. Close it, then retry.";

// Strip every non-digit so we can re-prepend a single leading "+" for E.164.
const NON_DIGIT_RE = /\D/g;

// Everything Call.sendDigits accepts: the 12 keypad characters plus "w", the
// SDK's 500 ms pause. Anything else makes it throw InvalidArgumentError.
const DTMF_ALLOWED_RE = /^[0-9*#w]+$/;

// Dev-only tracing for the call lifecycle. A Twilio failure carries its useful
// detail in `code` / `causes` / `explanation`, and the SDK raises `warning`
// events (e.g. constant-audio-input-level while muted) that the UI ignores
// entirely — none of it reaches the user, so without this a local reproduction
// leaves us guessing which side dropped the call.
const VOICE_DEBUG = import.meta.env.DEV;

const traceVoice = (event: string, detail?: unknown) => {
  if (!VOICE_DEBUG) return;
  // eslint-disable-next-line no-console
  console.info(`[softphone] ${event}`, detail ?? "");
};

// Inbound invites this browser has already declined. Twilio keeps a <Client>
// leg ringing until EVERY registered instance of the identity rejects it (or
// the 30s dial timeout expires), and its signaling re-delivers the still
// pending invite whenever that identity registers again — a transport blip, a
// wake-heal rebuild, a second tab. The SDK does not dedupe invites (each
// payload becomes a fresh Call in Device._onSignalingInvite), so the popup came
// back seconds after the user pressed Decline. Remembering the decline lets us
// re-reject it silently instead of ringing them again.
const DECLINED_CALL_MEMORY_MS = 5 * 60_000;
// Bounded so a long portal session cannot grow the map without limit.
const DECLINED_CALL_MEMORY_MAX = 20;

// Keyed on our own Call id when the custom parameters survived — stable across
// re-delivered invites for the same inbound call — and on the Twilio CallSid
// otherwise. Both are unique per call, so a genuine call-back from the same
// number is never suppressed.
const incomingCallKey = (call: TwilioCall) =>
  call.customParameters.get("call_id") || call.parameters.CallSid || "";

// Why an inbound invite that reached this browser was declined instead of
// shown: the device outlived its effect generation, presenting it threw, or
// the softphone reports a live call that has no card behind it.
type UnpresentedIncomingReason =
  | "generation-cancelled"
  | "handler-threw"
  | "busy-without-card";

// For the telemetry payload only. Reads the same fields incomingCallKey does,
// but must not throw: it runs on the path that reports a handler that already
// threw, possibly over these very fields.
const safeIncomingCallId = (call: TwilioCall): string | null => {
  try {
    return incomingCallKey(call) || null;
  } catch {
    return null;
  }
};

const rememberDeclinedCall = (
  declined: Map<string, number>,
  call: TwilioCall
) => {
  const key = incomingCallKey(call);
  if (!key) return;

  const now = Date.now();
  Array.from(declined.entries()).forEach(([entryKey, expiresAt]) => {
    if (expiresAt <= now) declined.delete(entryKey);
  });
  // Map keeps insertion order, so the overflow slice drops the oldest keys.
  Array.from(declined.keys())
    .slice(0, Math.max(0, declined.size + 1 - DECLINED_CALL_MEMORY_MAX))
    .forEach((entryKey) => declined.delete(entryKey));
  declined.set(key, now + DECLINED_CALL_MEMORY_MS);
};

const wasCallDeclined = (declined: Map<string, number>, call: TwilioCall) => {
  const key = incomingCallKey(call);
  const expiresAt = key ? declined.get(key) : undefined;
  if (expiresAt === undefined) return false;
  if (expiresAt > Date.now()) return true;

  declined.delete(key);
  return false;
};

interface TwilioErrorShape {
  name?: string;
  code?: number;
  message?: string;
  description?: string;
  explanation?: string;
  causes?: string[];
  solutions?: string[];
  originalError?: unknown;
}

const describeTwilioError = (err: unknown) => {
  const e = (err ?? {}) as TwilioErrorShape;
  return {
    name: e.name,
    code: e.code,
    message: e.message,
    description: e.description,
    explanation: e.explanation,
    causes: e.causes,
    solutions: e.solutions,
    originalError:
      e.originalError instanceof Error
        ? e.originalError.message
        : e.originalError
  };
};

// Friendly copy for known API rejection codes from POST /boom_crm/calls.
// Module-scoped: it has no component-scope dependencies, so there's no reason
// to rebuild this map on every render.
const API_ERROR_MESSAGES: Record<string, string> = {
  active_call_exists:
    "You already have a call in progress. Hang up before starting another.",
  destination_cooldown:
    "You just called this number — wait about 30 seconds and try again.",
  rate_limited_partner_hour: "Hourly call limit reached — try again later.",
  rate_limited_partner_day: "Daily call limit reached — try again tomorrow.",
  voice_not_enabled: "Your account doesn't have calling enabled.",
  no_voice_number: "No voice-enabled phone number is set up for your account."
};

// The one place that knows where the API hides its rejection code on an
// AxiosError — three call sites used to hand-roll this walk.
const apiErrorCode = (err: unknown): string | undefined => {
  if (!(err instanceof AxiosError)) return undefined;
  const code = err.response?.data?.error?.code;
  return typeof code === "string" ? code : undefined;
};

const friendlyApiMessage = (
  code: string | undefined,
  fallback: string
): string => (code && API_ERROR_MESSAGES[code]) || fallback;

// Coerce a user-entered number to E.164. boompay is US-only (the backend
// normalizes via Boom::PhoneNumber::US), so a bare 10-digit number is assumed
// to be US and gets a "1" country code rather than being treated as +<digits>
// (which would imply some other country, e.g. "+41…" for a Swiss number).
const toE164 = (raw: string): string => {
  const digits = raw.replace(NON_DIGIT_RE, "");
  // Bare US 10-digit number — prepend the US country code.
  if (digits.length === 10) return `+1${digits}`;
  // Already a US number with country code.
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  // Anything else (already-international, short codes, etc.) — re-add a single
  // leading "+" and let the backend validator have the final say.
  return `+${digits}`;
};

export function SoftphoneProvider({
  children,
  enabled
}: {
  children: React.ReactNode;
  enabled: boolean;
}) {
  const [status, setStatus] = useState<SoftphoneStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [deviceReady, setDeviceReady] = useState(false);
  const [callDurationSeconds, setCallDurationSeconds] = useState<number | null>(
    null
  );
  const [registrationAttempt, setRegistrationAttempt] = useState(0);

  const [errorAction, setErrorAction] =
    useState<SoftphoneErrorAction>("reconnect");

  const deviceRef = useRef<TwilioDevice | null>(null);
  const deviceReadyRef = useRef(false);
  const answeredAtRef = useRef<Date | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const refreshAttemptsRef = useRef(0);
  // One-shot `online` listener armed while we wait out an offline window
  // (wake-from-sleep, network blip) instead of burning retries (CRM-1073).
  const onlineHandlerRef = useRef<(() => void) | null>(null);
  // Its own timer, deliberately not refreshTimerRef: sharing that slot let a
  // token refresh silently cancel a pending recovery whose `online` listener
  // had already been removed, parking the softphone forever.
  const onlineGraceTimerRef = useRef<number | null>(null);
  // Rebuild backoff. `recoverAttemptsRef` indexes RECOVERY_BACKOFF_MS and is
  // reset only by a successful registration — a focus event must never reset it
  // or alt-tabbing during an outage turns into a request flood.
  const recoverAttemptsRef = useRef(0);
  const recoverTimerRef = useRef<number | null>(null);
  // Armed while we let the SDK's own reconnect try to fix a transport blip.
  const transportWatchdogRef = useRef<number | null>(null);
  // Set by retryRegistration so the device effect's cleanup can tell a rebuild
  // (dispose the old device, keep the UI) from a teardown (reset everything).
  const rebuildRef = useRef(false);
  // Cleanup closures capture the OLD `enabled`, so a rebuild that lands in the
  // same commit as the feature flipping off would wrongly skip the teardown
  // reset. The ref carries the post-render truth into the cleanup.
  const enabledRef = useRef(false);
  enabledRef.current = enabled;
  // True while a setup() generation is between start and settle. The wake-up
  // heal must not fire retryRegistration into that window — it would destroy
  // the in-flight device mid-register, and with an expired token every focus
  // event would do it again, starving recovery forever.
  const setupInFlightRef = useRef(false);
  // Dedupe for refreshToken: the SDK's `tokenWillExpire` and our wall-clock
  // timer share one deadline, and after system sleep both fire in the same
  // wake — without this that is two parallel token fetches, and on failure the
  // retry budget burns twice as fast.
  const refreshInFlightRef = useRef(false);
  // When the current fault started (ms epoch), so a fault that heals quickly
  // never surfaces. Cleared on every successful registration.
  const faultSinceRef = useRef<number | null>(null);
  // Allow-listed detail of the last fault, for the one telemetry event we emit
  // when a fault becomes user-visible.
  const faultDetailRef = useRef<DeviceFaultDetail | null>(null);
  const faultReportedRef = useRef(false);
  // A device fault that arrived while a call card was on screen. Call state
  // owns `status` in that window, so the fault is applied on dismissal.
  const pendingFaultRef = useRef<PendingFault | null>(null);
  // When the current capability token expires (ms epoch) — lets the wake-up
  // heal detect that the scheduled refresh slept through the expiry.
  const tokenExpiresAtRef = useRef<number | null>(null);
  const lastWakeHealRef = useRef(0);
  // Synchronous lock so two near-simultaneous placeCall calls can't both dial
  // (the `status` guard alone is an async-stale closure).
  const placingRef = useRef(false);
  // Mirrors `status` for async callbacks (e.g. the scheduled token refresh)
  // that would otherwise read a stale `status` from their closure.
  const statusRef = useRef<SoftphoneStatus>("idle");
  statusRef.current = status;
  // Same, for the wake-up heal: it must not re-register for a fault that
  // re-registering cannot fix (a stale build, calling switched off).
  const errorActionRef = useRef<SoftphoneErrorAction>("reconnect");
  errorActionRef.current = errorAction;
  // Inbound calls this browser declined, so a re-delivered invite for one of
  // them is rejected again instead of re-opening the popup.
  const declinedCallsRef = useRef(new Map<string, number>());

  // Mirrors `incomingCall` for the SDK event handlers (cancel/error arrive on
  // the Twilio call object, outside React's render cycle).
  const incomingCallRef = useRef<IncomingCall | null>(null);
  incomingCallRef.current = incomingCall;
  // Same reason, plus: device-fault handlers need to know a card is on screen
  // before they touch `status`, and they run outside the render cycle.
  const activeCallRef = useRef<ActiveCall | null>(null);
  activeCallRef.current = activeCall;
  // Status to restore when a ring ends unanswered — a terminal card (and its
  // Create-lead form) survives underneath the popup and must reappear as-is.
  const preIncomingStatusRef = useRef<SoftphoneStatus | null>(null);

  const markDeviceReady = (ready: boolean) => {
    deviceReadyRef.current = ready;
    setDeviceReady(ready);
  };

  // Is a call card (live, ringing or terminal) in front of the user right now?
  // While one is, call state owns `status` and no device-level event may write
  // it — describeCall models call statuses, so a device status renders as the
  // "Call interrupted" fallback and erases whatever the card was saying.
  // Reads the refs rather than state because every caller runs inside a Twilio
  // callback or a timer, i.e. before React has re-rendered. The clearing paths
  // (dismissCall, restorePreIncomingStatus) null the refs synchronously so this
  // never reports a card that has just gone away.
  const cardOnScreen = () =>
    !!activeCallRef.current || !!incomingCallRef.current;

  // --- Device lifecycle -----------------------------------------------------
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const setup = async () => {
      // A surfaced fault is sticky across background rebuild attempts. Without
      // this the recovery loop would flap the toast: every attempt would reset
      // the status (dismissing it) and every failure would raise it again.
      // And a card on screen owns `status` outright (same rule as applyFault):
      // a background rebuild must not relabel "Call ended · 02:31" as the
      // device's own state, which describeCall renders as "Call interrupted".
      if (!cardOnScreen() && statusRef.current !== "error") {
        setStatus("registering");
        setErrorMessage(null);
      }
      setupInFlightRef.current = true;

      // Held locally so the catch can destroy it. A device that is constructed
      // but never registered used to be unreachable from deviceRef (which was
      // only assigned after a SUCCESSFUL register), so nothing could ever
      // destroy it: its websocket stayed open, Twilio's infinite backoff kept
      // re-registering the same `agent-<id>` identity, and its still-bound
      // `incoming` listener meant inbound calls fanned out to zombie devices.
      let device: TwilioDevice | null = null;

      try {
        // Independent round trips — no reason to hold the token fetch behind
        // the SDK chunk download on first load.
        const [{ Device }, { token, expires_at }] = await Promise.all([
          import("@twilio/voice-sdk"),
          voiceApi.getCapabilityToken()
        ]);
        if (cancelled) return;

        // SDK's default codec preferences are correct for most networks and
        // avoid fighting the typed enum.
        device = new Device(token, {
          logLevel: VOICE_DEBUG ? "debug" : "warn",
          closeProtection: true,
          // Ask for the refresh well before expiry — the default 10s leaves no
          // room for a slow round trip.
          tokenRefreshMs: 60_000
        });
        // Own it from construction, so every exit path can destroy it.
        deviceRef.current = device;

        device.on("registered", () => {
          if (cancelled) return;
          markDeviceReady(true);
          resolveFault();
          setErrorMessage(null);
          // Same asymmetry to avoid as above: the card's own
          // reconnected/disconnect/cancel listeners drive its status, and
          // dismissCall restores "ready" from deviceReadyRef once it is gone.
          if (cardOnScreen()) return;
          setStatus("ready");
        });

        // The device's own view of its registration is the truth about whether
        // a call can be placed or received. Without these the UI could keep
        // advertising "ready" over a de-registered device, which turns an
        // outbound attempt into a backend Call that dies as expired_before_dial
        // and surfaces as the misleading "Can't reach this number".
        device.on("unregistered", () => {
          if (cancelled) return;
          traceVoice("device.unregistered");
          // Not merely bookkeeping: a graceful deregistration can arrive with
          // no error event at all, and without a fault noted here the softphone
          // would sit "ready"-but-dead forever (the wake-heal only reacts to
          // "error" or an expired token). Treated like a transport blip — the
          // SDK often re-registers by itself, so give it the grace window.
          noteTransportBlip({ name: "unregistered" });
        });

        device.on("destroyed", () => {
          if (cancelled) return;
          traceVoice("device.destroyed");
          // A destroyed device never comes back on its own (the SDK destroys on
          // pagehide, and a bfcache restore revives the page with a dead
          // device). Our own teardown/rebuild paths either set `cancelled`
          // first or clear this timer right after, so this only acts on
          // destroys we did not initiate.
          markDeviceReady(false);
          beginFault({ name: "destroyed" });
          scheduleRecovery();
        });

        // The SDK asks for a fresh token on its own schedule, anchored to a
        // live signaling connection. Our wall-clock timer stays as a backstop
        // (it is the only thing that covers a device whose socket is down).
        device.on("tokenWillExpire", () => {
          if (cancelled) return;
          traceVoice("device.token-will-expire");
          void refreshToken();
        });

        // Inbound call ringing this identity (CRM-905). The SDK auto-rejects
        // incoming calls while another call is live (allowIncomingWhileBusy
        // defaults to false), so this only fires when we can actually ring.
        //
        // The SDK starts the ringtone BEFORE it emits this event and stops it
        // only on accept, reject or cancel. Returning without rejecting used
        // to leave the user hearing a ring with nothing on screen to answer
        // for the whole 30s dial timeout, and an exception thrown in here was
        // swallowed by the SDK's promise chain, so it never reached Sentry
        // (CRM-1354). Every path that does not present the popup now rejects
        // the invite — the caller falls through to the other ringing agents —
        // and leaves one trace.
        device.on("incoming", (call: TwilioCall) => {
          if (cancelled) {
            rejectUnpresentedIncoming(call, "generation-cancelled");
            return;
          }
          try {
            handleIncoming(call);
          } catch (err) {
            rejectUnpresentedIncoming(call, "handler-threw", err);
          }
        });

        // The SDK passes the call a signaling error belongs to as the second
        // argument (Device.errorEvent), so a single leg's failure arrives here
        // looking like a device-wide one. Trace both before handling.
        device.on(
          "error",
          (err: Error & { code?: number }, call?: TwilioCall) => {
            if (cancelled) return;
            traceVoice("device.error", {
              ...describeTwilioError(err),
              hasCall: !!call,
              softphoneStatus: statusRef.current
            });
            handleDeviceError(err, call);
          }
        );

        await withTimeout(
          device.register(),
          REGISTRATION_TIMEOUT_MS,
          "registration timed out"
        );
        // The effect was torn down mid-registration: this generation no longer
        // owns anything, so hand nothing back and take the device with us.
        if (cancelled) {
          device.destroy();
          return;
        }
        refreshAttemptsRef.current = 0;

        scheduleTokenRefresh(new Date(expires_at));
      } catch (err) {
        // Destroy before reporting: a half-built device left alive is what
        // produced duplicate registrations for one identity.
        if (device) {
          device.destroy();
          if (deviceRef.current === device) deviceRef.current = null;
        }
        if (cancelled) return;
        handleDeviceError(err);
      } finally {
        // A cancelled generation must not clear the flag the newer one set.
        if (!cancelled) setupInFlightRef.current = false;
      }
    };

    setup();

    return () => {
      cancelled = true;
      clearRefreshTimer();
      clearOnlineHandler();
      clearRecoveryTimer();
      clearTransportWatchdog();
      deviceRef.current?.destroy();
      deviceRef.current = null;
      markDeviceReady(false);
      // A rebuild (retryRegistration bumping registrationAttempt) re-runs this
      // effect, and for that the cleanup's only job is disposing of the old
      // device. Resetting the visible state as well would wipe the fault the
      // user was just told about and blank any card on screen. A real teardown
      // — `enabled` flipping false, or unmount — still resets everything.
      // enabledRef carries the post-render value: a rebuild that lands in the
      // same commit as the feature turning off must still tear down fully, or
      // the toast/card would outlive a provider that will never run again.
      const isRebuild = rebuildRef.current && enabledRef.current;
      rebuildRef.current = false;
      if (isRebuild) return;
      // Close out the incident too. Without this, faultSinceRef survives a
      // disable/enable cycle and the first blip after re-enabling counts as
      // hours "down" — an instant toast that skips the whole grace window.
      recoverAttemptsRef.current = 0;
      faultSinceRef.current = null;
      faultDetailRef.current = null;
      faultReportedRef.current = false;
      pendingFaultRef.current = null;
      setErrorAction("reconnect");
      setErrorMessage(null);
      setActiveCall(null);
      setIncomingCall(null);
      setCallDurationSeconds(null);
      setStatus("idle");
    };
    // registrationAttempt is intentionally in the dep list so retryRegistration
    // can re-trigger the effect without tearing down the wrapper.
  }, [enabled, registrationAttempt]);

  // --- Wake-up heal (CRM-1073) -----------------------------------------------
  // Browser timers do not run while the system is suspended, so a long sleep
  // makes the scheduled refresh miss the token's expiry; Twilio then rejects
  // the stale registration the moment the machine wakes — while the browser is
  // already online. When the user returns to the tab, quietly rebuild the
  // device instead of greeting them with a stale "disconnected" toast.
  useEffect(() => {
    if (!enabled) return;

    const heal = () => {
      if (document.visibilityState === "hidden") return;
      const current = statusRef.current;
      if (LIVE_CALL_STATUSES.includes(current)) return;
      // Waiting on an explicit user action, or on something already in flight:
      // a recovery loop, an offline park, or the SDK's own reconnect. Healing on
      // top of any of those is how alt-tabbing during an outage used to become
      // a request flood — every focus reset the budget and fired a fresh
      // capability-token fetch, up to ~36 a minute per tab.
      if (current === "mic-denied" || current === "registering") return;
      if (
        setupInFlightRef.current ||
        recoverTimerRef.current ||
        onlineHandlerRef.current ||
        transportWatchdogRef.current
      )
        return;
      // Nothing retryable is pending — a stale build or a disabled account
      // cannot be healed by re-registering.
      if (errorActionRef.current !== "reconnect") return;
      const expiresAt = tokenExpiresAtRef.current;
      const tokenExpired = expiresAt !== null && Date.now() >= expiresAt;
      if (current !== "error" && !tokenExpired) return;
      // `focus` and `visibilitychange` fire together — heal at most once.
      if (Date.now() - lastWakeHealRef.current < WAKE_HEAL_COOLDOWN_MS) return;
      lastWakeHealRef.current = Date.now();
      traceVoice("wake.heal — re-registering", {
        status: current,
        tokenExpired
      });
      // Deliberately NOT resetting the backoff index: returning to the tab is
      // a hint that now is a good moment to try, not evidence the outage ended.
      refreshAttemptsRef.current = 0;
      retryRegistration();
    };

    window.addEventListener("focus", heal);
    document.addEventListener("visibilitychange", heal);
    return () => {
      window.removeEventListener("focus", heal);
      document.removeEventListener("visibilitychange", heal);
    };
    // retryRegistration and the refs it touches are stable across renders; the
    // listeners only need to rebind when the softphone is toggled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const clearRecoveryTimer = () => {
    if (recoverTimerRef.current) {
      window.clearTimeout(recoverTimerRef.current);
      recoverTimerRef.current = null;
    }
  };

  const clearTransportWatchdog = () => {
    if (transportWatchdogRef.current) {
      window.clearTimeout(transportWatchdogRef.current);
      transportWatchdogRef.current = null;
    }
  };

  // The device registered: whatever was wrong is over. Wipe the incident so the
  // next one starts with a full budget and a fresh "how long has this been
  // broken" clock.
  const resolveFault = () => {
    clearRecoveryTimer();
    clearTransportWatchdog();
    // Also disarm a parked online recovery — the SDK healing itself inside the
    // 2s online grace used to leave that timer live, which then destroyed the
    // freshly registered device and rebuilt it for nothing.
    clearOnlineHandler();
    recoverAttemptsRef.current = 0;
    refreshAttemptsRef.current = 0;
    faultSinceRef.current = null;
    faultDetailRef.current = null;
    faultReportedRef.current = false;
    pendingFaultRef.current = null;
    setErrorAction("reconnect");
  };

  // Stamps the incident bookkeeping shared by every fault path. The start time
  // survives across attempts so SUSTAINED_FAULT_MS measures the incident, not
  // the attempt.
  const beginFault = (detail: DeviceFaultDetail) => {
    markDeviceReady(false);
    faultDetailRef.current = detail;
    faultSinceRef.current = faultSinceRef.current ?? Date.now();
  };

  // Applies a terminal device state, unless a call card is on screen. Call state
  // owns `status` in that window: overwriting it used to blank a live call's
  // status line AND strip its End-call button (describeCall models call
  // statuses, not device ones), leaving the user unable to hang up a connected
  // call. The fault is applied when the card is dismissed instead.
  const applyFault = (next: SoftphoneStatus, message: string | null) => {
    if (cardOnScreen()) {
      pendingFaultRef.current = { status: next, message };
      traceVoice("device.fault-deferred — call on screen", { next });
      return;
    }
    setErrorMessage(message);
    setStatus(next);
  };

  // Counterpart of applyFault's deferral: called by every card-clearing path
  // AFTER its card ref is nulled. Returns true when a deferred fault landed and
  // now owns the status — the caller must not restore anything over it.
  const landPendingFault = (): boolean => {
    const pending = pendingFaultRef.current;
    pendingFaultRef.current = null;
    if (!pending) return false;
    setErrorMessage(pending.message);
    setStatus(pending.status);
    // mic-denied gets no recovery loop: registration doesn't need the mic, so
    // a rebuild would succeed, flip status to "ready" and auto-dismiss the mic
    // toast two seconds after the user finally got to see it. The mic toast's
    // own Retry button is the recovery there.
    if (pending.status === "error" && errorActionRef.current === "reconnect")
      scheduleRecovery();
    return true;
  };

  // Arms the next rebuild. Never gives up while the softphone is idle: the last
  // backoff rung repeats, so a device that is down for an hour comes back on
  // its own the moment the service does, with no user action.
  const scheduleRecovery = () => {
    // Already running, or parked waiting for connectivity.
    if (recoverTimerRef.current || onlineHandlerRef.current) return;
    // A card on screen means either a live leg (rebuilding would destroy it) or
    // a terminal card the user is still reading. dismissCall and
    // restorePreIncomingStatus pick this back up when the card goes away.
    if (cardOnScreen()) return;
    const attempt = recoverAttemptsRef.current;
    const rung =
      RECOVERY_BACKOFF_MS[Math.min(attempt, RECOVERY_BACKOFF_MS.length - 1)];
    // ±15% jitter: a fleet-wide outage otherwise synchronizes every open tab
    // onto the same 60s multiples — a small thundering herd against an API
    // that is already having a bad day.
    // eslint-disable-next-line sonarjs/pseudo-random -- retry jitter, not crypto
    const delay = Math.round(rung * (0.85 + Math.random() * 0.3));
    recoverAttemptsRef.current = attempt + 1;
    traceVoice("device.recovery-scheduled", { attempt, delay });
    recoverTimerRef.current = window.setTimeout(() => {
      recoverTimerRef.current = null;
      retryRegistration();
    }, delay);
  };

  // Records a fault and decides whether the user needs to hear about it yet.
  // A fault that clears inside SUSTAINED_FAULT_MS never surfaces at all — which
  // is the whole point: the routine case is a websocket blip that heals itself
  // in seconds, and a persistent red toast for it was the CRM-1073 complaint.
  const noteRecoverableFault = (detail: DeviceFaultDetail) => {
    beginFault(detail);
    const downForMs = Date.now() - (faultSinceRef.current ?? Date.now());

    if (downForMs >= SUSTAINED_FAULT_MS) {
      setErrorAction("reconnect");
      // NB: no "click Reconnect" instruction — this message also shows in
      // MakeCallModal after the toast (and its button) may have been dismissed,
      // and the background loop genuinely does keep retrying.
      applyFault(
        "error",
        "Lost connection to voice service — retrying automatically."
      );
      reportFault("recoverable", downForMs);
      scheduleRecovery();
      return;
    }

    // Still inside the grace window, so stay silent. "registering" renders no
    // toast and disables the call buttons, which is honest — the device really
    // cannot place a call right now.
    if (!cardOnScreen() && statusRef.current !== "error")
      setStatus("registering");

    scheduleRecovery();
  };

  // A fault retrying cannot fix. Terminal immediately, with copy that says what
  // to actually do, and no recovery loop to burn requests against.
  const noteTerminalFault = (
    action: SoftphoneErrorAction,
    message: string,
    detail: DeviceFaultDetail
  ) => {
    beginFault(detail);
    clearRecoveryTimer();
    clearTransportWatchdog();
    setErrorAction(action);
    applyFault("error", message);
    reportFault(action === "reload" ? "stale-build" : "blocked", 0);
  };

  // One telemetry event per incident. Everything else about the softphone is
  // dev-only console tracing, so a production failure currently leaves no
  // trace at all — which is why CRM-1073 had to be diagnosed by reading code.
  const reportFault = (kind: string, downForMs: number) => {
    if (faultReportedRef.current) return;
    faultReportedRef.current = true;
    // Allow-listed fields only. Never the error object itself: AxiosError's
    // toJSON serializes `config`, whose headers carry the portal bearer token.
    const payload = {
      kind,
      seconds_down: Math.round(downForMs / 1000),
      recovery_attempts: recoverAttemptsRef.current,
      ...faultDetailRef.current
    };
    logger.error("softphone.device_unavailable", payload);
    // The PostHog log channel has no grouping or alerting; Sentry is the only
    // sink in portal anyone gets paged from (same pairing as ErrorBoundary).
    Sentry.captureMessage("softphone.device_unavailable", {
      level: "warning",
      extra: payload
    });
  };

  const handleDeviceError = (err: unknown, call?: TwilioCall) => {
    const fault = classifyDeviceError(err);
    const detail = projectFaultDetail(err);
    traceVoice("device.fault", { kind: fault, ...detail });

    // The SDK delivers per-leg signaling errors on the DEVICE's error event
    // (the second argument identifies the owning call). The call's own
    // listeners already turn those into a call outcome, so touching device
    // state here is wrong twice over: it reports a leg failure as a dead
    // service, and for inbound it fires on every agent whose leg Twilio
    // cancelled because someone else answered. Checked before the mic branch
    // so a call-scoped mic error stays the call's problem too.
    if (call) {
      traceVoice("device.error-belongs-to-call — leaving device state alone");
      return;
    }

    if (fault === "mic-denied" || fault === "mic-unavailable") {
      markDeviceReady(false);
      // Through applyFault, not a bare setStatus: a device-level mic error
      // mid-call (USB headset unplugged) must not repaint the live card — the
      // leg often survives on the previous track, and stripping the End-call
      // button strands the user in a call they cannot hang up.
      applyFault(
        "mic-denied",
        fault === "mic-denied" ? MIC_DENIED_MESSAGE : MIC_UNAVAILABLE_MESSAGE
      );
      return;
    }

    // Offline blip (wake-from-sleep, dropped Wi-Fi): the whole app is
    // unreachable, so a "voice service disconnected" toast is just noise.
    // Hold the current UI and rebuild the device once connectivity returns.
    if (!navigator.onLine) {
      markDeviceReady(false);
      traceVoice("device.error-while-offline — deferring recovery to `online`");
      recoverWhenOnline();
      return;
    }

    if (fault === "stale-build") {
      noteTerminalFault(
        "reload",
        "Calling needs a page refresh to finish updating.",
        detail
      );
      return;
    }

    if (fault === "blocked") {
      noteTerminalFault(
        "none",
        friendlyApiMessage(
          detail.apiErrorCode,
          "Calling isn't available for your account right now."
        ),
        detail
      );
      return;
    }

    if (fault === "transport") {
      noteTransportBlip(detail);
      return;
    }

    noteRecoverableFault(detail);
  };

  // A transport error means the SDK is ALREADY reconnecting — its websocket
  // retries with backoff indefinitely and the device re-registers itself once
  // signaling is back. Rebuilding here would destroy exactly that recovery,
  // which is what the old 1s/5s ladder did: it turned a blip the SDK would
  // have healed silently into a terminal toast. Wait it out, and only take
  // over if the SDK has not managed it within the grace window. Also used for
  // a bare `unregistered` event, which has the same self-healing potential.
  const noteTransportBlip = (detail: DeviceFaultDetail) => {
    beginFault(detail);
    if (transportWatchdogRef.current || recoverTimerRef.current) return;
    transportWatchdogRef.current = window.setTimeout(() => {
      transportWatchdogRef.current = null;
      // Still not registered — the SDK's reconnect is not winning.
      if (deviceReadyRef.current) return;
      traceVoice("device.transport-grace-expired — taking over");
      noteRecoverableFault(detail);
    }, TRANSPORT_HEAL_GRACE_MS);
  };

  const retryRegistration = () => {
    clearRefreshTimer();
    clearOnlineHandler();
    clearRecoveryTimer();
    clearTransportWatchdog();
    // Destroying the device rejects every ringing call and disconnects the
    // active one, so a live leg must never reach this.
    if (LIVE_CALL_STATUSES.includes(statusRef.current)) {
      traceVoice("retryRegistration skipped — call in progress");
      return;
    }
    deviceRef.current?.destroy();
    deviceRef.current = null;
    markDeviceReady(false);
    rebuildRef.current = true;
    setRegistrationAttempt((n) => n + 1);
  };

  // --- Incoming calls (CRM-905) ----------------------------------------------

  // An inbound invite reached this browser but no popup will be shown for it.
  // Reject so the SDK stops the ringtone and Twilio moves on to the other
  // ringing agents, then report once: a ring with no UI is invisible in every
  // other channel — the API only sees a client leg that was never answered —
  // which is what made CRM-1354 undiagnosable after the fact.
  const rejectUnpresentedIncoming = (
    call: TwilioCall,
    reason: UnpresentedIncomingReason,
    err?: unknown
  ) => {
    try {
      call.reject();
    } catch (rejectErr) {
      traceVoice("incoming.reject-failed", describeTwilioError(rejectErr));
    }
    reportUnpresentedIncoming(call, reason, err);
  };

  // Allow-listed fields only, like reportFault: never the call or error
  // object itself.
  const reportUnpresentedIncoming = (
    call: TwilioCall,
    reason: UnpresentedIncomingReason,
    err?: unknown
  ) => {
    const twilio = describeTwilioError(err);
    const payload = {
      reason,
      call_id: safeIncomingCallId(call),
      softphone_status: statusRef.current,
      card_on_screen: cardOnScreen(),
      device_ready: deviceReadyRef.current,
      error_name: twilio.name,
      error_code: twilio.code,
      error_message: twilio.message
    };
    traceVoice("incoming.unpresented", payload);
    logger.error("softphone.incoming_unpresented", payload);
    Sentry.captureMessage("softphone.incoming_unpresented", {
      level: "warning",
      extra: payload
    });
  };

  const handleIncoming = (call: TwilioCall) => {
    // The user already declined this exact call and Twilio re-delivered the
    // invite (it stays pending until every registration of this identity
    // rejects it). Decline it again without disturbing them.
    if (wasCallDeclined(declinedCallsRef.current, call)) {
      traceVoice("incoming.rejected-declined-earlier", {
        callId: call.customParameters.get("call_id")
      });
      call.reject();
      return;
    }

    // Already showing an incoming popup (a second simultaneous inbound call) or
    // mid-call in a state the SDK's busy-reject didn't cover — decline so the
    // caller falls through to the other ringing agents.
    if (
      incomingCallRef.current ||
      LIVE_CALL_STATUSES.includes(statusRef.current)
    ) {
      traceVoice("incoming.rejected-busy", { status: statusRef.current });
      // A live status with no card behind it (and no dial in flight) is a
      // wedged softphone: every ring would be declined after a brief chirp
      // with nothing to answer, indefinitely. Still declined — reviving a
      // half-torn-down call state from inside a Twilio callback is riskier
      // than one missed ring — but no longer silently.
      if (!cardOnScreen() && !placingRef.current)
        reportUnpresentedIncoming(call, "busy-without-card");
      call.reject();
      return;
    }

    // The inbound TwiML endpoint passes call metadata as <Client><Parameter>
    // custom parameters; the caller number is also available natively.
    const params = call.customParameters;
    const callId = Number(params.get("call_id"));
    const leadId = Number(params.get("lead_id"));
    const incoming: IncomingCall = {
      twilioCall: call,
      callId: Number.isFinite(callId) && callId > 0 ? callId : undefined,
      fromNumber:
        params.get("from_number") || call.parameters.From || "Unknown",
      toNumber: params.get("to_number") || undefined,
      leadId: Number.isFinite(leadId) && leadId > 0 ? leadId : undefined,
      leadName: params.get("lead_name") || undefined,
      leadStage: params.get("lead_stage") || undefined,
      leadStageColor: params.get("lead_stage_color") || undefined,
      numberLabel: params.get("number_label") || undefined
    };

    traceVoice("incoming.ringing", {
      callId: incoming.callId,
      hasLead: !!incoming.leadId
    });

    // Caller hung up, or another registered tab/agent answered first — Twilio
    // cancels the rest. Only clear if this call is still the one ringing.
    call.on("cancel", () => {
      traceVoice("incoming.cancel", { callId: incoming.callId });
      clearIncoming(call);
    });
    call.on("error", (err: Error) => {
      traceVoice("incoming.error", describeTwilioError(err));
      clearIncoming(call);
    });

    // A terminal card (and its in-progress Create-lead form) deliberately
    // survives underneath — the popup only takes over the overlay visually.
    preIncomingStatusRef.current = statusRef.current;
    setIncomingCall(incoming);
    setStatus("incoming");
  };

  // Clears the incoming popup if `call` is still the ringing one (accept moves
  // the call into activeCall, after which cancel/error must not touch state).
  const clearIncoming = (call: TwilioCall) => {
    if (incomingCallRef.current?.twilioCall !== call) return;
    setIncomingCall(null);
    restorePreIncomingStatus();
  };

  const restorePreIncomingStatus = () => {
    const previous = preIncomingStatusRef.current;
    preIncomingStatusRef.current = null;
    // The ring is gone as far as this tick is concerned. incomingCallRef is
    // otherwise only refreshed on the next render, and the recovery scheduling
    // below reads it synchronously — leaving it stale would make
    // scheduleRecovery believe a card is still up and silently decline to run.
    incomingCallRef.current = null;
    if (statusRef.current !== "incoming") return;

    const terminalCardStatuses: SoftphoneStatus[] = [
      "no-answer",
      "call-ended",
      "unreachable"
    ];
    if (previous && terminalCardStatuses.includes(previous)) {
      setStatus(previous);
      return;
    }
    // A device fault raised while the popup was up was held back so it could not
    // repaint the ring (applyFault); the ring is gone, so it lands now. Without
    // this the device would stay dead with nothing on screen and no recovery
    // scheduled — scheduleRecovery also declines to run while a card is up.
    if (!activeCallRef.current) {
      if (landPendingFault()) return;
      if (!deviceReadyRef.current) scheduleRecovery();
    }
    // A fault the user was already looking at stays surfaced — restoring
    // anything milder here would dismiss the toast mid-incident only for the
    // next failed rebuild to raise it again (the flap the sticky rule forbids).
    if (previous === "error") {
      setStatus("error");
      return;
    }
    // "registering" rather than "idle" when the device is down: recovery really
    // is in progress, and it is the status MakeCallModal explains truthfully.
    setStatus(deviceReadyRef.current ? "ready" : "registering");
  };

  const acceptIncoming = () => {
    const incoming = incomingCallRef.current;
    if (!incoming) return;

    preIncomingStatusRef.current = null;
    setIncomingCall(null);
    setCallDurationSeconds(null);
    setErrorMessage(null);
    setStatus("connecting");

    // Bind BEFORE accept() so the `accept` event (→ in-call) is never missed.
    bindCallEvents(incoming.twilioCall, {
      callId: incoming.callId ?? 0,
      direction: "inbound",
      toNumber: incoming.fromNumber,
      leadId: incoming.leadId,
      startedAt: new Date(),
      twilioCall: incoming.twilioCall,
      fromNumber: incoming.toNumber,
      fromLabel: incoming.numberLabel,
      toLabel: incoming.leadName
    });
    incoming.twilioCall.accept();
  };

  const rejectIncoming = () => {
    const incoming = incomingCallRef.current;
    if (!incoming) return;
    traceVoice("incoming.rejected", { callId: incoming.callId });
    rememberDeclinedCall(declinedCallsRef.current, incoming.twilioCall);
    incoming.twilioCall.reject();
    setIncomingCall(null);
    restorePreIncomingStatus();
  };

  const attachLeadToActiveCall = (lead: { id: number; name?: string }) => {
    setActiveCall((c) =>
      c ? { ...c, leadId: lead.id, toLabel: c.toLabel || lead.name } : c
    );
  };

  // --- Token refresh -------------------------------------------------------
  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  // --- Offline recovery (CRM-1073) -------------------------------------------
  const clearOnlineHandler = () => {
    if (onlineHandlerRef.current) {
      window.removeEventListener("online", onlineHandlerRef.current);
      onlineHandlerRef.current = null;
    }
    if (onlineGraceTimerRef.current) {
      window.clearTimeout(onlineGraceTimerRef.current);
      onlineGraceTimerRef.current = null;
    }
  };

  // Park until the browser reports connectivity, then recover with a fresh
  // retry budget. A failure while offline must never count against the retry
  // ladder: after wake-from-sleep all 4 attempts burn through in ~80s before
  // the network is back, surfacing a terminal "disconnected" toast even though
  // the voice service is fine. Full re-registration is the recovery of choice
  // (the device's websocket and token are both stale after a long sleep) —
  // except during a live call, where destroying the device would kill the leg,
  // so we only refresh the token and let the SDK's own media/signaling
  // reconnection handle the rest.
  const recoverWhenOnline = () => {
    // Arm before checking connectivity so an `online` event that fires while we
    // are setting up cannot slip through the gap and park us forever.
    if (onlineHandlerRef.current) return;
    const handler = () => {
      clearOnlineHandler();
      onlineGraceTimerRef.current = window.setTimeout(() => {
        onlineGraceTimerRef.current = null;
        refreshAttemptsRef.current = 0;
        if (LIVE_CALL_STATUSES.includes(statusRef.current)) void refreshToken();
        else retryRegistration();
      }, ONLINE_RECOVERY_GRACE_MS);
    };
    onlineHandlerRef.current = handler;
    window.addEventListener("online", handler);
    // Connectivity came back between the failure and this call — the event has
    // already been and gone, so run the recovery ourselves.
    if (navigator.onLine) handler();
  };

  const scheduleTokenRefresh = (expiresAt: Date) => {
    clearRefreshTimer();
    const expiresAtMs = expiresAt.getTime();
    // A missing or unparseable `expires_at` yields NaN, and both Math.max(x,
    // NaN) and setTimeout(NaN) resolve to "fire immediately" — i.e. a refresh
    // hot loop against the API. Same for a clock skewed far enough into the
    // future to overflow setTimeout's signed 32-bit delay.
    const hasExpiry = Number.isFinite(expiresAtMs);
    tokenExpiresAtRef.current = hasExpiry ? expiresAtMs : null;
    const delay = hasExpiry
      ? Math.min(
          MAX_TIMER_DELAY_MS,
          Math.max(
            TOKEN_REFRESH_MIN_DELAY_MS,
            expiresAtMs - Date.now() - TOKEN_REFRESH_BUFFER_MS
          )
        )
      : TOKEN_REFRESH_FALLBACK_DELAY_MS;
    // NB: this deadline is best-effort only — browser timers do not advance
    // while the system is suspended, so a long sleep pushes the refresh past
    // the token's real expiry. The device's own `tokenWillExpire` event and the
    // wake-up heal effect cover that gap.
    refreshTimerRef.current = window.setTimeout(refreshToken, delay);
  };

  const refreshToken = async () => {
    if (!deviceRef.current) return;
    // Two schedulers share this deadline (the SDK's `tokenWillExpire` and our
    // wall-clock timer), and after system sleep both fire in the same wake.
    // One fetch is enough; the loser's would be pure waste and its failure
    // would double-burn the retry budget.
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    clearRefreshTimer();

    try {
      const { token, expires_at } = await voiceApi.getCapabilityToken();
      if (!deviceRef.current) return;
      deviceRef.current.updateToken(token);
      refreshAttemptsRef.current = 0;
      scheduleTokenRefresh(new Date(expires_at));
    } catch (err) {
      // Offline — don't burn the retry budget on a window where every attempt
      // is guaranteed to fail; wait for the `online` event instead.
      if (!navigator.onLine) {
        recoverWhenOnline();
        // eslint-disable-next-line no-console
        console.warn(
          "[softphone] token refresh failed while offline — waiting for connectivity"
        );
        return;
      }

      const attempt = refreshAttemptsRef.current;
      refreshAttemptsRef.current = attempt + 1;

      if (attempt < TOKEN_REFRESH_RETRY_DELAYS_MS.length)
        refreshTimerRef.current = window.setTimeout(
          refreshToken,
          TOKEN_REFRESH_RETRY_DELAYS_MS[attempt]
        );
      else {
        // Retries exhausted — stop scheduling. Don't tear down the UI while a
        // call leg is still up: the Twilio connection survives a stale
        // capability token, so forcing "error" here would blank the live call
        // card mid-conversation. When a call is in progress, just flag the
        // refresh failure and keep the live-call UI; only surface the terminal
        // "error" state (for ReconnectBanner's manual retry) when idle.
        clearRefreshTimer();
        if (LIVE_CALL_STATUSES.includes(statusRef.current))
          // eslint-disable-next-line no-console
          console.warn(
            "[softphone] token refresh exhausted during a live call — keeping the call UI"
          );
        // Hand off to the same fault policy as a device error: rebuild with
        // backoff, and only tell the user if it stays broken.
        else noteRecoverableFault(projectFaultDetail(err));
      }
      // Log only the message — the raw axios error's config.headers can carry
      // the capability/Authorization token.
      // eslint-disable-next-line no-console
      console.warn(
        "[softphone] token refresh failed",
        err instanceof Error ? err.message : String(err)
      );
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  // --- Place call -----------------------------------------------------------
  const placeCall: SoftphoneContextValue["placeCall"] = async ({
    toNumber,
    leadId,
    contactId,
    customerId,
    fromNumberId,
    fromLabel,
    toLabel
  }) => {
    if (!deviceRef.current || !deviceReadyRef.current) {
      setErrorMessage("Softphone is not ready yet");
      return;
    }
    // A dial is already being placed (rapid double-fire) — ignore silently.
    if (placingRef.current) return;
    if (LIVE_CALL_STATUSES.includes(status)) {
      toast.error(
        "You already have a call in progress. Hang up before starting another."
      );
      return;
    }
    placingRef.current = true;

    // Coerce to E.164 (US default): strip ALL formatting, then add the "1"
    // country code for a bare 10-digit number, so both "(408) 334-1502" and
    // "+1 (408) 334-1502" satisfy the api's `to_number` validator (E164_REGEX).
    const normalizedTo = toE164(toNumber);

    // Clear any terminal card left over from the previous call.
    setCallDurationSeconds(null);
    answeredAtRef.current = null;
    setStatus("connecting");
    setErrorMessage(null);

    try {
      const { call, dial_token } = await voiceApi.authorizeCall({
        to_number: normalizedTo,
        lead_id: leadId,
        contact_id: contactId,
        customer_id: customerId,
        provisioned_phone_number_id: fromNumberId,
        idempotency_key: crypto.randomUUID()
      });

      const twilioCall = await deviceRef.current.connect({
        params: {
          call_id: String(call.id),
          dial_token,
          to_number: normalizedTo
        }
      });

      bindCallEvents(twilioCall, {
        callId: call.id,
        direction: "outbound",
        toNumber: normalizedTo,
        leadId,
        startedAt: new Date(),
        twilioCall,
        fromNumber: call.from_number,
        fromLabel,
        toLabel
      });
    } catch (err) {
      handlePlaceCallError(err);
    } finally {
      placingRef.current = false;
    }
  };

  const handlePlaceCallError = (err: unknown) => {
    if (isMicPermissionError(err)) {
      setStatus("mic-denied");
      markDeviceReady(false);
      setErrorMessage(
        isMicDeniedError(err) ? MIC_DENIED_MESSAGE : MIC_UNAVAILABLE_MESSAGE
      );
      return;
    }
    const friendly = friendlyApiMessage(
      apiErrorCode(err),
      "Couldn't start the call — please try again."
    );
    toast.error(friendly);
    setErrorMessage(friendly);
    // Authorization/connect failed before a call leg existed — return to ready.
    setStatus(deviceReadyRef.current ? "ready" : "error");
  };

  // Resolves the authoritative call outcome from the backend after Twilio
  // disconnects, so the card can show No answer / Call ended · MM:SS /
  // Can't reach this number. The activeCall is intentionally kept on screen
  // until the user dismisses it.
  const resolveOutcome = async (
    callId: number,
    direction: ActiveCall["direction"]
  ) => {
    const answeredAt = answeredAtRef.current;
    const localDuration = answeredAt
      ? Math.max(0, Math.round((Date.now() - answeredAt.getTime()) / 1000))
      : 0;

    const fallback = () => {
      // No backend answer — infer from whether the call ever connected.
      if (answeredAt) {
        setCallDurationSeconds(localDuration);
        setStatus("call-ended");
      } else {
        setCallDurationSeconds(null);
        setStatus("no-answer");
      }
    };

    // An accepted inbound call always ends as "Call ended · MM:SS" — the
    // outbound terminal states (No answer / Can't reach) describe dialing
    // failures that can't happen once we've answered. callId 0 means the TwiML
    // custom parameters were dropped, so there is nothing to fetch.
    if (direction === "inbound" || !callId) {
      if (direction === "inbound") {
        setCallDurationSeconds(localDuration);
        setStatus("call-ended");
        return;
      }
      fallback();
      return;
    }

    try {
      const summary = await voiceApi.fetchCall(callId);
      const mapped = OUTCOME_STATUS_MAP[summary.status];
      if (mapped === "call-ended") {
        setCallDurationSeconds(summary.duration_seconds ?? localDuration);
        setStatus("call-ended");
        return;
      }
      if (mapped) {
        setCallDurationSeconds(null);
        setStatus(mapped);
        return;
      }
      // Status not yet terminal (webhook race) — best-effort inference.
      fallback();
    } catch {
      fallback();
    }
  };

  const bindCallEvents = (twilioCall: TwilioCall, call: ActiveCall) => {
    setActiveCall(call);
    // Read the starting value off the call rather than assuming false — the SDK
    // only emits `mute` on a change, so nothing would correct a wrong guess.
    setIsMuted(twilioCall.isMuted());
    setCallDurationSeconds(null);
    answeredAtRef.current = null;

    // The SDK's own mute state is the source of truth for the toggle. Call.mute()
    // emits this only when the underlying track actually flipped (it guards on
    // wasMuted !== isMuted), so the button can never show a state the media never
    // took. The SDK re-applies the current setting when the input device changes
    // or after an ICE restart, passing the existing value — those stay silent.
    twilioCall.on("mute", (muted: boolean) => {
      traceVoice("call.mute", { muted, callStatus: twilioCall.status() });
      setIsMuted(muted);
    });

    twilioCall.on("accept", () => {
      const answeredAt = new Date();
      answeredAtRef.current = answeredAt;
      traceVoice("call.accept", { callId: call.callId });
      setActiveCall((c) => (c ? { ...c, answeredAt } : c));
      setStatus("in-call");
    });
    twilioCall.on("reconnecting", (err?: unknown) => {
      traceVoice("call.reconnecting", describeTwilioError(err));
      setStatus("reconnecting");
    });
    twilioCall.on("reconnected", () => {
      traceVoice("call.reconnected");
      setStatus("in-call");
    });
    twilioCall.on("disconnect", (disconnected?: unknown) => {
      traceVoice("call.disconnect", {
        callId: call.callId,
        // `status()` distinguishes a local hangup from a remote/gateway one.
        twilioStatus: (disconnected as { status?: () => string })?.status?.(),
        answered: !!answeredAtRef.current,
        secondsSinceAccept: answeredAtRef.current
          ? Math.round((Date.now() - answeredAtRef.current.getTime()) / 1000)
          : null
      });
      setStatus("ending");
      void resolveOutcome(call.callId, call.direction);
    });
    // A canceled call emits ONLY `cancel` — no `disconnect` follows (the SDK
    // skips it for cancellations). Happens when the caller hangs up (or
    // another agent answers) in the window between our accept() and the
    // bridge; without this handler the UI would wedge in connecting/in-call.
    twilioCall.on("cancel", () => {
      traceVoice("call.cancel", { callId: call.callId });
      setStatus("ending");
      void resolveOutcome(call.callId, call.direction);
    });
    twilioCall.on("error", (err: Error) => {
      traceVoice("call.error", describeTwilioError(err));
      if (isMicPermissionError(err)) {
        // accept()'s getUserMedia failed — surface the mic state (with the
        // right recovery instruction), not a dialing failure. The card is
        // cleared here, so the direct status write cannot clobber one.
        setActiveCall(null);
        activeCallRef.current = null;
        markDeviceReady(false);
        setErrorMessage(
          isMicDeniedError(err) ? MIC_DENIED_MESSAGE : MIC_UNAVAILABLE_MESSAGE
        );
        setStatus("mic-denied");
        return;
      }
      // A call-leg error means we never reached the destination.
      setErrorMessage(err.message);
      setCallDurationSeconds(null);
      setStatus("unreachable");
    });
    // Not acted on — traced only. Twilio raises constant-audio-input-level and
    // bytes-sent warnings in exactly the muted state we're investigating.
    twilioCall.on("warning", (name: string, data?: unknown) =>
      traceVoice("call.warning", { name, data })
    );
    twilioCall.on("warning-cleared", (name: string) =>
      traceVoice("call.warning-cleared", { name })
    );
  };

  const hangup = () => {
    activeCall?.twilioCall.disconnect();
  };

  const toggleMute = () => {
    if (!activeCall) return;
    // Intentionally no setIsMuted: the `mute` listener in bindCallEvents owns the
    // state. Tracing the request separately from the resulting event is what
    // distinguishes "the click never reached the SDK" from "the SDK declined".
    traceVoice("call.mute-requested", { requested: !isMuted });
    activeCall.twilioCall.mute(!isMuted);
  };

  // DTMF for anything that answers with a menu — call-screening ("press 1 to
  // accept"), voicemail PINs, "press 2 for leasing" (CRM-1190). The SDK also
  // plays each tone locally, so the user hears that it went out. Returns
  // whether the tone was actually handed to the SDK: the pad's readout must not
  // claim a digit the call never received.
  const sendDtmf = (digits: string): boolean => {
    const twilioCall = activeCall?.twilioCall;
    if (!twilioCall) return false;
    // sendDigits throws InvalidArgumentError on anything outside this set.
    if (!DTMF_ALLOWED_RE.test(digits)) return false;

    try {
      traceVoice("call.dtmf", { digits, callStatus: twilioCall.status() });
      twilioCall.sendDigits(digits);
      return true;
    } catch (err) {
      traceVoice("call.dtmf-failed", describeTwilioError(err));
      toast.error("Couldn't send the tone — try again.");
      return false;
    }
  };

  const dismissCall = () => {
    // Dismiss must never leave a connected leg running with no UI to end it.
    // Normally the card is already terminal so this is a no-op (the SDK ignores
    // disconnect() on a closed call), but a device fault can put a card with a
    // still-live leg in front of the Dismiss affordance.
    activeCall?.twilioCall.disconnect();
    setActiveCall(null);
    activeCallRef.current = null;
    setCallDurationSeconds(null);
    setIsMuted(false);
    answeredAtRef.current = null;

    // A device fault held back while the card was up (applyFault) lands now.
    if (landPendingFault()) return;
    if (deviceReadyRef.current) {
      setStatus("ready");
      return;
    }
    // The device died while the card was on screen and recovery was deferred to
    // avoid erasing it — pick it back up now that the card is gone, and say so
    // rather than leaving a stale terminal status with no card behind it.
    setStatus("registering");
    scheduleRecovery();
  };

  const isReady = deviceReady && !LIVE_CALL_STATUSES.includes(status);

  const value = useMemo<SoftphoneContextValue>(
    () => ({
      status,
      errorMessage,
      errorAction,
      activeCall,
      incomingCall,
      isMuted,
      isReady,
      callDurationSeconds,
      placeCall,
      acceptIncoming,
      rejectIncoming,
      hangup,
      toggleMute,
      sendDtmf,
      dismissCall,
      retryRegistration,
      attachLeadToActiveCall
    }),
    // placeCall/hangup/etc. close over `status`, `deviceReady`, `activeCall` and
    // `callDurationSeconds`; recompute the value whenever any of them change so
    // consumers never call a stale closure.
    [
      status,
      errorMessage,
      errorAction,
      activeCall,
      incomingCall,
      isMuted,
      isReady,
      callDurationSeconds
    ]
  );

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
}

export function useSoftphone(): SoftphoneContextValue {
  const ctx = useContext(SoftphoneContext);
  if (!ctx)
    throw new Error("useSoftphone must be used within <SoftphoneProvider>");

  return ctx;
}

// --- Helpers --------------------------------------------------------------

// What kind of thing went wrong, and therefore who should fix it: the SDK (it
// is already reconnecting), us (rebuild with a fresh token), or the user
// (re-grant the mic, reload the page, ask an admin to enable calling).
type DeviceFaultKind =
  | "mic-denied"
  | "mic-unavailable"
  | "transport"
  | "blocked"
  | "stale-build"
  | "recoverable";

interface DeviceFaultDetail {
  code?: number | string;
  name?: string;
  message?: string;
  http_status?: number;
  apiErrorCode?: string;
}

interface PendingFault {
  status: SoftphoneStatus;
  message: string | null;
}

function classifyDeviceError(err: unknown): DeviceFaultKind {
  if (isMicDeniedError(err)) return "mic-denied";
  if (isMicUnavailableError(err)) return "mic-unavailable";

  if (err instanceof AxiosError) {
    const status = err.response?.status;
    // 401 only reaches us after the axios interceptor already tried a token
    // refresh and dispatched auth:session-expired, so a logout is on its way;
    // 403 means the feature or the permission is off. Retrying neither helps.
    if (status === 401 || status === 403) return "blocked";
    return "recoverable";
  }

  const message = err instanceof Error ? err.message : "";
  if (STALE_BUILD_MESSAGE_RE.test(message)) return "stale-build";

  const code = (err as TwilioErrorShape | null)?.code;
  if (typeof code === "number" && TRANSPORT_ERROR_CODES.has(code))
    return "transport";
  // Everything else — including token errors (20101-20104, 31201-31205) — is
  // recoverable: the rebuild mints a fresh token, which is exactly their fix.
  return "recoverable";
}

// Allow-listed projection for telemetry. Deliberately never touches an axios
// error's `config` or `response.headers` — `config.headers` carries the portal
// bearer token, and AxiosError.toJSON would happily serialize it.
function projectFaultDetail(err: unknown): DeviceFaultDetail {
  const twilio = describeTwilioError(err);
  const detail: DeviceFaultDetail = {
    code: twilio.code,
    name: twilio.name,
    message: twilio.message
  };
  if (err instanceof AxiosError) {
    detail.http_status = err.response?.status;
    detail.apiErrorCode = apiErrorCode(err);
  }
  return detail;
}

// `Device.register()` never rejects on its own if signaling opens but
// registration never completes, so it needs an external deadline.
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error(`[softphone] ${label}`)),
      ms
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function matchesMicCodes(err: unknown, codes: Set<number | string>): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; code?: number | string };
  if (e.code !== undefined && codes.has(e.code)) return true;
  if (e.name !== undefined && codes.has(e.name)) return true;
  return false;
}

function isMicDeniedError(err: unknown): boolean {
  return matchesMicCodes(err, MIC_DENIED_CODES);
}

function isMicUnavailableError(err: unknown): boolean {
  return matchesMicCodes(err, MIC_UNAVAILABLE_CODES);
}

// Either mic outcome blocks a call, so the dial paths treat them alike.
function isMicPermissionError(err: unknown): boolean {
  return isMicDeniedError(err) || isMicUnavailableError(err);
}
